import { shelfLabel } from '../shelves.js';
import { bookPalette } from './palette.js';
import { HEARTS } from './sprites.js';

// How this theme turns a book or a shelf into type and art: the lettering the
// font will accept, the shelf names short enough for a sign, a sprite at a
// whole multiple of its own size, and the two drawings that stand for a book
// once it is off its shelf - its cover and its rating.
//
// A module of its own rather than the bottom of one of the two files that use
// it. plateLabel and sizedSprite are genuinely shared - the room's status bar
// and the sheet's own bar name a shelf the same way, and the room's marker and
// the sheet's hearts are the same kind of sprite at different sizes - and the
// rest is here because the file that draws a room should not also be carrying
// the rules about curly apostrophes.

// Long enough to name the shelf, short enough to fit on a brass plate. The
// classic shelf's labels are sentence-shaped and do not.
const PLATE_LABELS = {
  'currently-reading': 'READING',
  'to-read': 'TO READ',
  read: 'READ',
  'did-not-finish': 'D.N.F.',
};
// A sign sits on its own bookcase rather than over a whole shelf, so it has to
// fit one: nine characters is what a 77-pixel bookcase holds. Goodreads shelf
// names are whatever the reader typed, so the cap is not theoretical - the
// account this was measured on has SPOOKY-BOOKS and NYT-BEST-OF-21ST-CENTURY.
//
// Trimmed after the cut, not before: `NYT BEST OF...` falls exactly on a space
// at nine, and a plate with a trailing space on it is a word that is not
// centred on its own sign.
const PLATE_LIMIT = 9;
export const plateLabel = (shelfKey) =>
  (shelfKey && PLATE_LABELS[shelfKey])
  || shelfLabel(shelfKey || '').toUpperCase().slice(0, PLATE_LIMIT).trim();

// A sprite at its own size, or at a whole multiple of it. The multiple is what
// lets the same six-pixel heart stand on a bookcase's status bar and on a
// full-screen sheet without a second drawing of it: an integer scale under
// image-rendering: pixelated is still exact squares, and a fractional one is
// a row of grey seams.
export const sizedSprite = (sprite, zoom = 1) => ({
  width: `${sprite.width * zoom}px`,
  height: `${sprite.height * zoom}px`,
  backgroundImage: sprite.url,
  backgroundSize: '100% 100%',
  imageRendering: 'pixelated',
});

// A strip of frames (see strip() in sprites.js) at its own size or a whole
// multiple of it, showing one frame at a time. --eb-strip is the whole strip's
// width, which the eb-frames keyframes in eightbit.css step through.
export const stripStyle = (sprite, zoom = 1) => ({
  ...sizedSprite(sprite, zoom),
  backgroundSize: `${sprite.width * sprite.frames * zoom}px ${sprite.height * zoom}px`,
  '--eb-strip': `${sprite.width * sprite.frames * zoom}px`,
});

// Everything printed in this theme is upper case and reduced to characters the
// font actually has. A curly apostrophe out of a Goodreads title renders as a
// blank box otherwise, which on a screen this deliberate reads as a fault
// rather than as a character.
const PRINTABLE = /[^A-Z0-9 .,:;!?'"()#&\/+-]/g;
export const shout = (text) => String(text || '')
  .toUpperCase()
  .replace(/[‘’ʼ′]/g, "'")
  .replace(/[“”]/g, '"')
  .replace(/[‐-―]/g, '-')
  .replace(/…/g, '...')
  .replace(PRINTABLE, '')
  .trim();

// A rating out of five as five hearts, halves included - the shape the console
// games showed, and the reason this is hearts rather than stars.
export function Hearts({ rating, zoom = 1 }) {
  const halves = Math.round(Math.max(0, Math.min(5, rating)) * 2);
  return (
    <span className="eb-hearts" style={{ gap: `${zoom}px` }} aria-label={`${(halves / 2).toFixed(1)} out of 5`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const filled = halves - i * 2;
        const sprite = filled >= 2 ? HEARTS.full : filled === 1 ? HEARTS.half : HEARTS.empty;
        return <i key={i} className="eb-heart" style={sizedSprite(sprite, zoom)} />;
      })}
    </span>
  );
}

// The book as an object rather than as a spine on a shelf. Drawn from the same
// four colours its spine is, so it is recognisably the book the reader just
// stopped in front of and not a generic one.
//
// Its parts are in the 24x34 units the art was drawn in whatever size it is
// printed at; the caller scales the whole thing by a whole number, so a cover
// on a full-screen sheet is the same drawing four times the size rather than a
// second, larger drawing that would have to be kept in step.
export function BookPortrait({ book }) {
  const palette = bookPalette(book);
  // A cover is a spine, two rules and some lettering too small to read - which
  // at twenty-four pixels across is all of it there is room to say, and enough
  // for the thing to read as a book rather than as a coloured rectangle.
  return (
    <div className="eb-portrait" style={{ background: palette.face, borderColor: palette.dark }}>
      <span className="eb-portrait-spine" style={{ background: palette.dark }} />
      <span className="eb-portrait-rule" style={{ background: palette.light }} />
      <span className="eb-portrait-rule eb-portrait-rule--low" style={{ background: palette.light }} />
      <span className="eb-portrait-line eb-portrait-line--title" style={{ background: palette.ink }} />
      <span className="eb-portrait-line eb-portrait-line--sub" style={{ background: palette.ink }} />
      <span className="eb-portrait-line eb-portrait-line--by" style={{ background: palette.ink }} />
    </div>
  );
}
